import { useServicesContext } from "../../providers/ServicesProvider";
import { IAccessContext } from "../../services/UserService";
import useSWR from "swr";
import { UserGroup } from "../../domain/UserGroup";
import { Collection } from "../../domain/Collection";
import { useProducts } from "../useProducts/useProducts";
import { useAttributes } from "../useAttributes/useAttributes";
import { useEffect, useRef, useState } from "react";
import { OrganizationPermission, ProductPermission } from "../../domain/Permission.interface";
import {
    filterPermissionListsForContext,
    getOrgPermissionsFromGroupList,
    getProjects,
    orgPermissionListToAccessList
} from "../../services/AccessListTransforms";
import { ALL_PAGES_SIZE, SWR_KEY_PREFIX } from "../../constants/constants";
import { User } from "../../domain/User";

export const useUserAccessList = ({
    organizationId,
    user,
    context
}: {
    organizationId?: string;
    user?: User | null;
    context?: IAccessContext;
}): {
    accessList: ProductPermission[] | undefined;
    error: Error | undefined;
    isLoading: boolean | undefined;
} => {
    const { userService } = useServicesContext();
    const { products } = useProducts({ organizationId, includeServices: true });
    const { getExperimentationProjects } = useAttributes({ isActive: true });
    const [accessList, setAccessList] = useState<ProductPermission[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean | undefined>();
    const lastFetched = useRef<string | null>(null);

    const key =
        organizationId && user?.email
            ? `${
                  SWR_KEY_PREFIX.GROUPS
              }/api/usergroups/?organizationId=${organizationId}&email=${user?.email}&${user?.userGroupIds.join(",")}`
            : null;

    const { data: accessData, error: accessDataError } = useSWR<Collection<UserGroup>, Error>(key, () => {
        return userService.getUserGroups({
            organizationId: organizationId!,
            emails: [user?.email!],
            size: ALL_PAGES_SIZE
        });
    });

    useEffect(() => {
        if (lastFetched.current !== user?.email) {
            setIsLoading(true);
            lastFetched.current = user?.email || null;
        }
    }, [user?.email, accessData]);

    useEffect(() => {
        if (accessDataError) {
            setIsLoading(false);
            lastFetched.current = null;
        }
    }, [accessDataError]);

    useEffect(() => {
        const populateData = async (
            groups: Collection<UserGroup> | undefined
        ): Promise<ProductPermission[] | undefined> => {
            if (groups && products) {
                let orgPermissions: OrganizationPermission[] | undefined = getOrgPermissionsFromGroupList(
                    groups.items,
                    products
                );

                setIsLoading(true);
                //get projects for EXP instances
                await Promise.all(getProjects(orgPermissions, getExperimentationProjects));

                //filter by context scope and items
                orgPermissions = filterPermissionListsForContext(context!, orgPermissions);

                //turn into nested product/instance/project list
                return orgPermissionListToAccessList(orgPermissions);
            } else {
                return Promise.resolve(undefined);
            }
        };

        // create flat list of all permissions for the user based on their group membership
        if (accessData && !accessDataError) {
            populateData(accessData)
                .then((result: ProductPermission[] | undefined) => {
                    setAccessList(result);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [context, getExperimentationProjects, products, accessData, accessDataError]);

    return {
        accessList,
        isLoading: isLoading || (!accessList && !accessDataError),
        error: accessDataError
    };
};
